import React from 'react';
import {
  BadgeDollarSignIcon,
  IdCardIcon,
  SmartphoneIcon,
  WalletCardsIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { DigitalIdPreview } from './DigitalIdPreview';

const images = [
  {
    src: '/photo_2026-07-06_02-03-08.jpg',
    alt: 'iVilotid visar anslutna taxiappar som Uber och Bolt',
  },
  {
    src: '/photo_2026-07-07_21-35-46.jpg',
    alt: 'iVilotid ekonomivy med transaktioner från anslutna appar',
  },
  {
    src: '/photo_2026-07-03_16-17-50.jpg',
    alt: 'iVilotid Money Manager-vy för inkomster och utgifter',
  },
];

const benefits = [
  { icon: SmartphoneIcon, title: 'Taxiappar', text: 'Alla appar på ett ställe.' },
  { icon: BadgeDollarSignIcon, title: 'Ekonomi', text: 'Inkomster och utgifter samlat.' },
  { icon: IdCardIcon, title: 'Digitalt ID', text: 'Dina uppgifter och din taxilegitimation nära.' },
  { icon: WalletCardsIcon, title: 'DG-plånbok', text: 'Dina viktiga papper — lätta att hitta.' },
];

export function DriverEcosystem() {
  return (
    <section id="foraren" className="bg-[#F4F8FA] py-14 sm:py-16">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[.9fr_1.1fr] lg:items-end lg:gap-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
              Allt för föraren
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.08] tracking-[-.04em] sm:text-4xl">
              Flera appar — en samlad vardag.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-7 text-slate-700">
              iVilotid samlar allt du behöver för att köra, ta rast och sköta
              ekonomin. Ditt digitala ID och dina viktiga papper finns nära —
              före, under och mellan resorna.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {benefits.map(({ icon: Icon, title, text }, index) => (
              <motion.article
                key={title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.28,
                  delay: index * 0.04,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="min-w-0 rounded-xl border border-[#0B1120]/10 bg-white p-3.5"
              >
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#ECFEFF] text-[#007EAF]">
                  <Icon size={16} aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-bold">{title}</p>
                <p className="mt-1 text-sm leading-5 text-slate-700">{text}</p>
              </motion.article>
            ))}
          </div>
        </div>
        <div className="mt-8 overflow-hidden rounded-2xl border border-[#06B6D4]/20 bg-[#EAF5F8] p-5 sm:p-6">
          <div className="flex flex-col gap-3 border-b border-[#0B1120]/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.1em] text-[#006D98]">
                Från resa till överblick
              </p>
              <p className="mt-1.5 font-display text-xl leading-6 tracking-[-.025em]">
                All viktig information — utan att byta system.
              </p>
            </div>
            <p className="flex items-center gap-2 text-xs font-bold text-[#006D98]">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5C400]" aria-hidden="true" />
              Produktvyer från iVilotid
            </p>
          </div>
          <div className="mx-auto mt-5 grid max-w-3xl grid-cols-3 items-end justify-items-center gap-3 sm:gap-5">
            {images.map((image, index) => (
              <motion.figure
                key={image.src}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{
                  duration: 0.32,
                  delay: index * 0.05,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`w-full min-w-0 ${index === 1 ? 'max-w-[200px]' : 'max-w-[155px]'}`}
              >
                <div className="aspect-[9/19] overflow-hidden rounded-[1.15rem] border-[4px] border-[#0B1120] bg-white shadow-[0_8px_18px_rgba(11,17,32,.14)]">
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </motion.figure>
            ))}
          </div>
          <DigitalIdPreview />
        </div>
      </div>
    </section>
  );
}
